import React, { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLoader, faCircleXmark, faSearchPlus, faSearchMinus } from "@fortawesome/pro-solid-svg-icons";

// Set up the worker for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export default function ViewFile({ file }) {
    const [numPages, setNumPages] = useState(null);
    const [containerWidth, setContainerWidth] = useState(null);
    const [scale, setScale] = useState(1.0);
    const [loadError, setLoadError] = useState(null);
    const [passwordCancelled, setPasswordCancelled] = useState(false);
    const containerRef = useRef(null);

    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
        setLoadError(null);
    }

    function onDocumentLoadError(error) {
        console.error("PDF load error:", error);
        setLoadError(error);
    }

    function onPassword(callback, reason) {
        if (passwordCancelled) {
            return null;
        }

        if (reason === 2) {
            setPasswordCancelled(true);
            setLoadError({ message: "password", name: "PasswordException" });
            return null;
        }

        const password = prompt("Enter PDF password:");

        if (password === null) {
            setPasswordCancelled(true);
            setLoadError({ message: "password", name: "PasswordException" });
            return null;
        }

        return callback(password);
    }

    useEffect(() => {
        setPasswordCancelled(false);
        setLoadError(null);
    }, [file.url]);

    useEffect(() => {
        function updateWidth() {
            if (containerRef.current) {
                // Subtracting padding/scrollbar
                setContainerWidth(containerRef.current.clientWidth - 40);
            }
        }

        // Initial width
        updateWidth();
        // Also use a timeout to check width again after transition
        const timer = setTimeout(updateWidth, 500);

        // Listen for resize
        window.addEventListener("resize", updateWidth);
        return () => {
            window.removeEventListener("resize", updateWidth);
            clearTimeout(timer);
        };
    }, []);

    return (
        <div className="flex flex-col h-full w-full">
            {/* Zoom Controls */}
            <div className="flex justify-end p-2 bg-white border-b border-gray-200">
                <button onClick={() => setScale((s) => Math.max(0.5, s - 0.1))} className="p-2 text-gray-600 hover:text-primary transition-colors" title="Zoom Out">
                    <FontAwesomeIcon icon={faSearchMinus} />
                </button>
                <span className="p-2 text-sm text-gray-500">{Math.round(scale * 100)}%</span>
                <button onClick={() => setScale((s) => Math.min(2.0, s + 0.1))} className="p-2 text-gray-600 hover:text-primary transition-colors" title="Zoom In">
                    <FontAwesomeIcon icon={faSearchPlus} />
                </button>
            </div>

            {/* PDF Container */}
            <div
                ref={containerRef}
                style={{
                    flex: 1,
                    width: "100%",
                    height: "calc(100vh - 250px)",
                    overflowY: "auto",
                    backgroundColor: "#525659",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "20px",
                }}
            >
                {passwordCancelled || loadError ? (
                    <div className="flex flex-col items-center justify-center p-10 bg-white rounded-lg shadow-lg">
                        <FontAwesomeIcon icon={faCircleXmark} className="text-3xl text-red-500 mb-2" />
                        <p className="text-gray-900 font-medium">
                            {loadError?.message?.includes("password") || loadError?.name === "PasswordException" ? "This PDF is password protected" : "Failed to load PDF preview"}
                        </p>
                    </div>
                ) : (
                    <Document
                        file={file.url}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        onPassword={onPassword}
                        loading={
                            <div className="flex flex-col items-center justify-center p-10">
                                <FontAwesomeIcon icon={faLoader} spin className="text-3xl text-white mb-2" />
                                <span className="text-white">Loading PDF...</span>
                            </div>
                        }
                        error={
                            <div className="flex flex-col items-center justify-center p-10 bg-white rounded-lg shadow-lg">
                                <FontAwesomeIcon icon={faCircleXmark} className="text-3xl text-red-500 mb-2" />
                                <p className="text-gray-900 font-medium">
                                    {loadError?.message?.includes("password") || loadError?.name === "PasswordException" ? "This PDF is password protected." : "Failed to load PDF preview."}
                                </p>
                                <p className="text-gray-600 text-sm mt-2">Please download the file to view it.</p>
                                <a href={file.url} target="_blank" rel="noopener noreferrer" className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors">
                                    Download File
                                </a>
                            </div>
                        }
                    >
                        {Array.from(new Array(numPages), (el, index) => (
                            <div key={`page_${index + 1}`} className="mb-4 shadow-lg">
                                <Page pageNumber={index + 1} width={containerWidth ? containerWidth * scale : undefined} renderTextLayer={false} renderAnnotationLayer={false} loading="" />
                            </div>
                        ))}
                    </Document>
                )}
            </div>
        </div>
    );
}
